/**
 * Editor de plantilla admin (Sprint 6 E).
 *
 * Modo `create` (/admin/surveys/new) o `edit` (/admin/surveys/:id/edit).
 * El editor combina:
 *   - Formulario para metadata (name, description, code en create)
 *   - Textarea con schema JSON validable contra SurveyTemplateSchema
 *     (Zod) en cliente; el server vuelve a validar.
 *   - Preview con score-sim opcional vendra en sprint posterior.
 *
 * Versionado: si la plantilla esta publicada, el PATCH retornaria 409
 * `template_published`; tendremos un boton "Crear v(n+1)" que clona
 * el schema actual con un nuevo draft. Para MVP del Sprint 6 dejamos
 * un mensaje claro al usuario indicando que hay que crear una nueva
 * version manualmente — el flujo "create v(n+1)" es Sprint 7 backlog.
 */
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import { SurveyTemplateSchema, categoryForCode } from '@assessos/shared';

import { ApiError } from '../api/client.js';
import { type AdminTemplateDetail, adminSurveysApi } from '../api/admin-surveys.js';

type Mode = 'create' | 'edit';

export function AdminSurveyEditorPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const mode: Mode = id ? 'edit' : 'create';
  const navigate = useNavigate();
  const { setBackTo } = useAppShell();

  // Back button (BB-UX-01)
  useEffect(() => {
    setBackTo({ href: '/admin/surveys', label: 'Plantillas' });
    return () => setBackTo(null);
  }, [setBackTo]);
  const [tpl, setTpl] = useState<AdminTemplateDetail | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schemaText, setSchemaText] = useState('');
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    adminSurveysApi
      .get(id)
      .then((t) => {
        setTpl(t);
        setCode(t.code);
        setName(t.name);
        setDescription(t.description ?? '');
        setSchemaText(JSON.stringify(t.schema, null, 2));
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Error'));
  }, [mode, id]);

  function validateSchema(): unknown {
    setSchemaError(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(schemaText);
    } catch (err) {
      setSchemaError(`JSON invalido: ${err instanceof Error ? err.message : 'parse error'}`);
      return null;
    }
    const v = SurveyTemplateSchema.safeParse(parsed);
    if (!v.success) {
      const issue = v.error.issues[0];
      setSchemaError(
        issue ? `${issue.path.join('.') || 'schema'}: ${issue.message}` : 'Schema invalido',
      );
      return null;
    }
    return v.data;
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const validated = validateSchema();
    if (validated === null) return;
    setSaving(true);
    try {
      if (mode === 'create') {
        const created = await adminSurveysApi.create({
          code: code.trim(),
          name: name.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          schema: validated,
        });
        navigate(`/admin/surveys/${created.id}/edit`, { replace: true });
        setInfo('Plantilla creada como draft.');
      } else if (id) {
        const update: Parameters<typeof adminSurveysApi.update>[1] = {
          name: name.trim(),
          schema: validated,
        };
        if (description.trim()) update.description = description.trim();
        const updated = await adminSurveysApi.update(id, update);
        setTpl((t) => (t ? { ...t, ...updated } : t));
        setInfo('Cambios guardados.');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === 'template_published') {
          setError(
            'Esta plantilla esta publicada. Para cambios funcionales debes crear una nueva version (publica primero un draft v(n+1)).',
          );
        } else {
          setError(`${err.code}${err.status === 400 ? ' — revisa el schema' : ''}`);
        }
      } else {
        setError(err instanceof Error ? err.message : 'Error guardando');
      }
    } finally {
      setSaving(false);
    }
  }

  // Sprint Y #4 — Warning si el code quedará sin categorizar.
  // Calculado en vivo cuando cambia el campo code (solo en modo create,
  // donde el usuario puede modificarlo).
  const categoryWarning = useMemo<string | null>(() => {
    if (mode !== 'create' || !code.trim()) return null;
    const cat = categoryForCode(code.trim());
    if (cat === 'other') {
      return (
        'Esta plantilla quedará sin categorizar. Revisa el `code` o pide al equipo extender ' +
        '`categoryForCode()` antes de guardar.'
      );
    }
    return null;
  }, [code, mode]);

  async function handlePublish(): Promise<void> {
    if (!id) return;
    if (!confirm('Publicar esta plantilla? Sera utilizable en assessments nuevos.')) return;
    setError(null);
    setInfo(null);
    setPublishing(true);
    try {
      const r = await adminSurveysApi.publish(id);
      setTpl((t) => (t ? { ...t, ...r } : t));
      setInfo('Plantilla publicada.');
    } catch (err) {
      if (err instanceof ApiError) setError(err.code);
      else setError(err instanceof Error ? err.message : 'Error publicando');
    } finally {
      setPublishing(false);
    }
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400">Admin · plantillas</p>
          <h1 className="text-xl font-semibold text-brand-900">
            {mode === 'create'
              ? 'Nueva plantilla'
              : `${tpl?.name ?? 'Editar'} v${String(tpl?.version ?? '?')}`}
          </h1>
        </div>
        {mode === 'edit' && tpl && (
          <div className="flex items-center gap-2">
            <span
              className={
                'rounded-full px-2 py-1 text-xs font-medium ' +
                (tpl.is_published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800')
              }
            >
              {tpl.is_published ? 'publicada' : 'draft'}
            </span>
            {!tpl.is_published && (
              <button
                type="button"
                disabled={publishing}
                onClick={() => {
                  void handlePublish();
                }}
                className="rounded-lg bg-green-700 px-4 py-2 text-xs font-medium text-white disabled:bg-slate-300"
              >
                {publishing ? 'Publicando…' : 'Publicar'}
              </button>
            )}
          </div>
        )}
      </header>

      {info && (
        <p className="rounded bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
          {info}
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
        >
          {error}
        </p>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label htmlFor="code" className="block text-sm font-medium text-slate-700">
              Code
            </label>
            <input
              id="code"
              type="text"
              required
              disabled={mode === 'edit'}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              pattern="[a-z][a-z0-9_]+"
            />
            <p className="text-[10px] text-slate-400">
              Identificador permanente: minusculas, numeros y guion bajo.
            </p>
            {/* Sprint Y #4 — Warning inline NO bloqueante si code queda en 'other' */}
            {categoryWarning && (
              <p
                role="alert"
                className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
              >
                ⚠ {categoryWarning}
              </p>
            )}
          </div>
          <div className="space-y-1">
            <label htmlFor="name" className="block text-sm font-medium text-slate-700">
              Nombre legible
            </label>
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="description" className="block text-sm font-medium text-slate-700">
            Descripcion
          </label>
          <input
            id="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="schema"
            className="flex items-center justify-between text-sm font-medium text-slate-700"
          >
            <span>Schema (JSON)</span>
            <button
              type="button"
              onClick={() => {
                validateSchema();
              }}
              className="text-xs font-normal text-brand-700 hover:underline"
            >
              Validar
            </button>
          </label>
          <textarea
            id="schema"
            rows={24}
            value={schemaText}
            onChange={(e) => {
              setSchemaText(e.target.value);
              setSchemaError(null);
            }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            spellCheck={false}
          />
          {schemaError && (
            <p role="alert" className="text-xs text-red-700">
              {schemaError}
            </p>
          )}
          <p className="text-[10px] text-slate-400">
            Estructura validada con SurveyTemplateSchema (mismo Zod que en server). La version se
            asigna automaticamente: la primera vez =1; al crear v(n+1) sobre una publicada, +1.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate('/admin/surveys')}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Volver
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300"
          >
            {saving ? 'Guardando…' : mode === 'create' ? 'Crear draft' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </main>
  );
}
